import React from 'react';
import { Box, Grid, GridProps, IconButton } from '@mui/material';
import html2canvas from 'html2canvas';
import { useDuplicateBoardNode, useUpdateBoardNode } from 'hooks/Board';
import { BoardNodes } from 'types/BoardNode';
import { copyToClipboard, parseJSONCleaned } from 'utilities/index';
import BoardNodeHeader from './BoardNodeHeader';
import BoardNodeFooter from './BoardNodeFooter';
import { ActionOptions } from 'types/common';
import jsPDF from 'jspdf';
import {
  AudioNode,
  FileNode,
  GraphNode,
  ImageNode,
  Mermaid,
  TiptapEditor,
  VideoNode,
} from './BoardNodeTypes';
import { BOARD_ID_SELECTORS } from './helper/constants';
import BoardNodeExpandedView from './BoardNodeExpandedView';
import { useBoardNodeTipTapEditor } from 'external/TipTapEditor/hooks';
import BoardNodeConnectionError from './helper/BoardNodeConnectionError';
import NodeOptionsBubbleMenu from './NodeOptionsBubbleMenu';
import type { BoardNodeType } from 'types/entityTypes/boardNodeTypes';
import { useDialog, usePopover } from 'hooks/common';
import { ListOptionsType } from 'types/common/DropdownOption';
import { ToggleOn } from '@mui/icons-material';
import DeleteMemoryQA from 'components/SVG/DeleteMemoryQA';
import { DuplicateIcon } from 'components/SVG';
import DownloadIcon from 'components/SVG/DownloadIcon';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import InsertLinkIcon from '@mui/icons-material/InsertLink';

export type BoardNodeContainerProps = {
  id?: string;
  boardNode: BoardNodes;
  nodeType: BoardNodeType | undefined;
  readOnly: boolean;
  containerProps?: GridProps;
  isCanvasView?: boolean;
  isHover?: boolean;
  shouldResolveReference?: boolean;
  onUpdateNode?: (node: BoardNodes) => void;
  onDeleteNode?: (nodeID: string) => void;
  onDuplicateNode?: (node: BoardNodes) => void;
  isLinked?: boolean;
  isError?: boolean;
  onError?: (errorMessage: string) => void;
  getLastFocusedElement?: (
    event:
      | React.MouseEvent<HTMLElement, MouseEvent>
      | React.MouseEvent<HTMLDivElement, MouseEvent>,
    id: string
  ) => void;
  isNodeSelected?: boolean;
  boardNodeTitle?: string;
};

function BoardNodeContainer({
  id,
  boardNode,
  nodeType,
  readOnly = false,
  containerProps = {
    border: (theme) => `1px solid ${theme.palette.borderColor}`,
  },
  isCanvasView = true,
  shouldResolveReference = false,
  isHover,
  onUpdateNode,
  onDeleteNode,
  onDuplicateNode,
  isLinked = false,
  isError,
  onError,
  getLastFocusedElement,
  boardNodeTitle,
  isNodeSelected = false,
}: BoardNodeContainerProps) {
  const [nodeSpecificOptions, setNodeSpecificOptions] = React.useState<
    ListOptionsType[]
  >([]);
  const boardNodeContentContainerID = `${BOARD_ID_SELECTORS.SPARK_BOARD_NODE_CONTENT}-${boardNode.id}`;
  const { open, openDialog, closeDialog } = useDialog();
  const { mutateAsync: duplicateBoardNode } = useDuplicateBoardNode(
    boardNode.attributes.boardID
  );
  const { mutateAsync: updateBoardNode } = useUpdateBoardNode(
    boardNode.id,
    boardNode.attributes.boardID
  );

  // Copy to clipboard
  const [currentTabTextContent, setCurrentTabTextContent] =
    React.useState<string>('');
  const nodRef = React.useRef<HTMLSpanElement | null>(null);
  const [chartWidth, setChartWidth] = React.useState<number>(0);

  // Get initial content
  const initialValueContent = shouldResolveReference
    ? boardNode?.meta?.dataResolved
    : boardNode?.meta?.data;

  // Use the TipTap editor hook
  const {
    handleChange: handleContentChange,
    handleEditorBlur: handleEditorBlurEvent,
    handleEditorFocus: handleEditorFocusEvent,
    setupEditorFocusHandler,
  } = useBoardNodeTipTapEditor({
    boardNodeId: boardNode.id,
    boardId: boardNode.attributes.boardID,
  });

  React.useEffect(() => {
    if (!nodRef.current) {
      return;
    }
    setCurrentTabTextContent(nodRef.current.innerText || '');
    const handleResize = () => {
      if (nodRef.current) {
        setChartWidth(nodRef.current.offsetWidth);
      }
    };

    const observer = new ResizeObserver(handleResize);
    observer.observe(nodRef.current);

    handleResize();
    return () => observer.disconnect();
  }, []);

  // Set up the editor focus handler
  setupEditorFocusHandler(nodRef, boardNodeContentContainerID);

  const isReadOnly = boardNode.attributes.readOnly;
  // Only one copy of handleTitleChange
  const handleTitleChange = async (title: string) => {
    if (readOnly) {
      return;
    }
    if (onUpdateNode) {
      const updatedNode = {
        ...boardNode,
        attributes: { ...boardNode.attributes, title: title },
      };
      onUpdateNode(updatedNode);
    }
    updateBoardNode({ title: title });
  };

  const handleReadOnlyClick = React.useCallback(() => {
    updateBoardNode({ readOnly: !isReadOnly });
    if (onUpdateNode) {
      const updatedNode = {
        ...boardNode,
        attributes: { ...boardNode.attributes, readOnly: !isReadOnly },
      };
      onUpdateNode(updatedNode);
    }
  }, [boardNode, isReadOnly, onUpdateNode, updateBoardNode]);

  const handleDeleteClick = React.useCallback(async () => {
    if (onDeleteNode) {
      onDeleteNode(boardNode.id);
    }
    updateBoardNode({ isDeleted: true });
  }, [boardNode.id, onDeleteNode, updateBoardNode]);

  const getBoardNodeContent = (
    readOnly: boolean,
    isLinked: boolean,
    onError?: (errorMessage: string) => void,
    isError?: boolean
  ) => {
    if (!nodeType) {
      return <></>;
    }

    if (isError) {
      return (
        <BoardNodeConnectionError
          isSparkOutputPreview={shouldResolveReference}
          sparkStepID={boardNode.attributes.sparkStepID}
        />
      );
    }
    const _handleContentChange = async (content: string) => {
      handleContentChange(content);
      if (onUpdateNode) {
        onUpdateNode({
          ...boardNode,
          meta: { ...boardNode.meta, data: content },
        });
      }
    };
    // Simplified parsing logic - handle various content formats safely

    switch (nodeType.attributes.key) {
      case 'text':
        return (
          <TiptapEditor
            initialContent={initialValueContent}
            onChange={_handleContentChange}
            readOnly={isLinked || readOnly}
            onFocus={handleEditorFocusEvent}
            onBlur={handleEditorBlurEvent}
            onError={onError}
            setNodeSpecificOptions={setNodeSpecificOptions}
          />
        );
      case 'text-experimental':
        return (
          <TiptapEditor
            initialContent={initialValueContent}
            onChange={_handleContentChange}
            readOnly={isLinked || readOnly}
            onFocus={handleEditorFocusEvent}
            onBlur={handleEditorBlurEvent}
            onError={onError}
          />
        );
      case 'mermaid':
        return (
          <Mermaid
            initialContent={JSON.stringify(initialValueContent)}
            readOnly={isLinked || readOnly}
            onChange={handleContentChange}
            onError={onError}
          />
        );
      case 'graph':
        return (
          <GraphNode
            initialContent={initialValueContent}
            onChange={handleContentChange}
            readOnly={isLinked || readOnly}
            onError={onError}
            width={chartWidth}
            minWidth={`${chartWidth}`}
          />
        );
      case 'image':
        return (
          <ImageNode
            initialContent={initialValueContent}
            setNodeSpecificOptions={setNodeSpecificOptions}
            onError={onError}
          />
        );
      case 'video':
        return (
          <VideoNode
            initialContent={initialValueContent}
            setNodeSpecificOptions={setNodeSpecificOptions}
            onError={onError}
          />
        );
      case 'audio':
        return (
          <AudioNode
            initialContent={initialValueContent}
            setNodeSpecificOptions={setNodeSpecificOptions}
            onError={onError}
          />
        );
      case 'file':
        return (
          <FileNode
            initialContent={initialValueContent}
            setNodeSpecificOptions={setNodeSpecificOptions}
          />
        );
      default:
        <></>;
    }
  };

  const handleDuplicate = React.useCallback(async () => {
    const response = await duplicateBoardNode({ boardNodeID: boardNode.id });
    if (onDuplicateNode) {
      onDuplicateNode(response.data);
    }
  }, [boardNode.id, duplicateBoardNode, onDuplicateNode]);

  const handleCopy = React.useCallback(() => {
    copyToClipboard(currentTabTextContent);
  }, [currentTabTextContent]);

  const handleDownloadClick = async (): Promise<void> => {
    if (nodRef.current) {
      const element = nodRef.current;

      try {
        // Get the dimensions of the DOM element
        const { offsetWidth: width, offsetHeight: height } = element;

        // Set the desired scale for high resolution
        // const scale = 0.5; // Higher scale for HD output
        const pdfWidth = width * (96 / 72); // Convert px to pt (1pt = 1/72 inch, 1px = 1/96 inch)
        const pdfHeight = height * (96 / 72);

        // Convert the DOM element to a high-resolution canvas
        const canvas = await html2canvas(element, {
          // scale, // Increases pixel density
          useCORS: true, // Enables cross-origin images
        });

        const imageData = canvas.toDataURL('image/png');

        // Create a new PDF with standard dimensions
        const pdf = new jsPDF({
          orientation: 'landscape',
          unit: 'pt', // Use points for consistent sizing
          format: [pdfWidth, pdfHeight],
        });

        // Add the high-resolution image to the PDF
        pdf.addImage(imageData, 'PNG', 0, 0, pdfWidth, pdfHeight);

        // Download the PDF
        pdf.save('download.pdf');
      } catch (error) {
        console.error('Error generating PDF:', error);
      }
    }
  };

  const suggestions = parseJSONCleaned<ActionOptions[]>(
    boardNode.attributes.suggestions
  );
  const options: ListOptionsType[] = React.useMemo(() => {
    if (!isCanvasView) {
      return [
        {
          label: 'Read Only',
          icon: isReadOnly ? <ToggleOn /> : <ToggleOffIcon />,
          onClick: handleReadOnlyClick,
          tag: 'Read_Only',
        },
        {
          label: 'Delete',
          icon: <DeleteMemoryQA color='black' />,
          onClick: handleDeleteClick,
          tag: 'delete',
        },
      ];
    }
    return [
      {
        label: 'Copy',
        icon: <DuplicateIcon color='black' />,
        onClick: handleCopy,
        tag: 'copy',
      },
      {
        label: 'Duplicate',
        icon: <DuplicateIcon color='black' />,
        onClick: handleDuplicate,
        tag: 'duplicate',
      },
      {
        label: 'Delete',
        icon: <DeleteMemoryQA color='black' width='20' height='20' />,
        onClick: handleDeleteClick,
        tag: 'delete',
      },
    ];
  }, [
    handleCopy,
    handleDeleteClick,
    handleDuplicate,
    handleReadOnlyClick,
    isCanvasView,
    isReadOnly,
  ]);
  // Check if the node type supports urls and determine if file is internal
  const supportURLs = nodeType?.attributes?.acceptsURLInput;
  const [externalURL, setExternalURL] = React.useState<string>('');

  const isInternalFile: boolean = React.useMemo(() => {
    if (!supportURLs) return true;
    try {
      const metaData = boardNode?.meta?.data;
      const parsedData = JSON.parse(metaData as string);
      const files = parsedData?.files;

      const fileUrl = files[0]?.url;
      setExternalURL(fileUrl);
      return fileUrl?.startsWith(
        'https://sparkupstorageuat.blob.core.windows.net'
      );
    } catch (error) {
      console.error('Error checking if file is internal:', error);
      return true;
    }
  }, [nodeType?.attributes?.key, boardNode?.meta?.data, supportURLs]);

  const fileTypeSpecificOptions = React.useMemo(() => {
    if (isInternalFile) {
      return (
        <IconButton onClick={handleDownloadClick}>
          <DownloadIcon color='black' width='20' height='20' />
        </IconButton>
      );
    }
    return (
      <IconButton onClick={() => window.open(externalURL, '_blank')}>
        <InsertLinkIcon sx={{ color: 'black' }} />
      </IconButton>
    );
  }, [handleDownloadClick, isInternalFile]);

  const {
    anchorEl,
    open: isPopoverOpen,
    closePopover,
    openPopover,
  } = usePopover();

  const handleFocus = (event: React.FocusEvent<HTMLDivElement>) => {
    event.stopPropagation();
    openPopover(event.currentTarget);
  };

  const handleBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    if (event.target?.closest(BOARD_ID_SELECTORS.SPARK_BOARD_NODE_CONTAINER)) {
      return;
    }
    closePopover();
  };

  return (
    <Grid
      container
      {...containerProps}
      id={id}
      sx={{
        borderRadius: '10px',
        ...containerProps?.sx,
      }}
      tabIndex={1}
      onFocus={handleFocus}
      onBlur={handleBlur}
    >
      <Box
        width='100%'
        display='flex'
        flexDirection={'column'}
        gap={1}
        position={'relative'}
        className={BOARD_ID_SELECTORS.SPARK_BOARD_NODE_CONTAINER}
      >
        <NodeOptionsBubbleMenu
          isReadOnly={isReadOnly}
          options={{ default: options, nodeSpecific: nodeSpecificOptions }}
          isReadOnlyMode={readOnly}
          isCanvasView={isCanvasView}
          onExpandClick={openDialog}
          isPopoverOpen={isPopoverOpen}
          anchorEl={anchorEl}
          closePopover={closePopover}
          fileTypeSpecificOptions={fileTypeSpecificOptions}
        />
        <>
          <Grid
            item
            component='span'
            width='100%'
            key={boardNode.id}
            id={boardNodeContentContainerID}
            borderRadius='0 0 10px 10px'
            ref={nodRef}
            overflow='hidden'
            className='board-node-content'
          >
            <BoardNodeHeader
              title={boardNode?.attributes.title || ''}
              onTitleSave={handleTitleChange}
              isReadOnlyMode={readOnly}
            />
            <Grid
              item
              component='span'
              width='100%'
              key={boardNode.id}
              id={boardNodeContentContainerID}
              borderRadius='0 0 10px 10px'
              ref={nodRef}
              onClick={(e) =>
                getLastFocusedElement?.(e, boardNodeContentContainerID)
              }
            >
              {getBoardNodeContent(readOnly, isLinked, onError, isError)}
            </Grid>
            {suggestions && suggestions.length > 0 && (
              <BoardNodeFooter suggestions={suggestions} />
            )}
          </Grid>
        </>
      </Box>
      {open && (
        <BoardNodeExpandedView
          onClose={closeDialog}
          open={open}
          boardNodeTitle={boardNodeTitle || ''}
          boardID={boardNode.attributes.boardID}
          boardNode={boardNode}
          onTitleSave={handleTitleChange}
          boardNodeOptions={
            nodeType?.attributes?.key === 'text' ? nodeSpecificOptions : []
          }
        >
          {getBoardNodeContent(false, false)}
        </BoardNodeExpandedView>
      )}
    </Grid>
  );
}

export default React.memo(
  BoardNodeContainer,

  // need to rethink the attributes that are critical to the board node
  (prevProps, nextProps) => {
    // Only re-render if critical props change

    return (
      prevProps.boardNode.id === nextProps.boardNode.id &&
      prevProps.boardNode.meta?.data === nextProps.boardNode.meta?.data &&
      prevProps.boardNode.meta?.dataResolved ===
        nextProps.boardNode.meta?.dataResolved &&
      prevProps.boardNodeTitle === nextProps.boardNodeTitle &&
      prevProps.readOnly === nextProps.readOnly &&
      prevProps.isHover === nextProps.isHover &&
      prevProps.isCanvasView === nextProps.isCanvasView &&
      prevProps.nodeType === nextProps.nodeType
    );
  }
);
